// The legend popover (opened by the (i) toolbar button): biome swatches plus the marker,
// visibility and border key. Same information the old always-visible legend carried.

import { BIOME_COLORS, FOG_COLOR } from './biomeColors.js';
import type { MinimapLabels } from './types.js';

export interface MinimapLegendProps {
  readonly id: string;
  readonly labels: MinimapLabels;
}

function Swatch({ className, style }: { className: string; style?: React.CSSProperties }) {
  return <span className={`inline-block size-2.5 shrink-0 ${className}`} style={style} />;
}

export function MinimapLegend({ id, labels }: MinimapLegendProps) {
  return (
    <section
      id={id}
      aria-label={labels.legendTitle}
      data-testid="minimap-legend"
      data-appdom-id="minimap-legend"
      data-appdom-type="Popover"
      data-appdom-scope="aside"
      data-appdom-label={labels.legendTitle}
      className="absolute inset-x-2 top-8 z-20 flex flex-col gap-1.5 rounded-md border border-border/70 bg-popover/95 p-2 text-[11px] text-popover-foreground shadow-lg backdrop-blur-sm max-md:top-12"
    >
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {labels.legendTitle}
      </h4>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
        {Object.entries(BIOME_COLORS).map(([name, color]) => (
          <span key={name} className="inline-flex items-center gap-1">
            <Swatch className="rounded-sm" style={{ backgroundColor: color }} />
            {labels.biomes[name] ?? name}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 border-t border-border/40 pt-1">
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-[3px] border border-foreground/60 bg-foreground/90" />
          {labels.city}
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-full border-2 border-foreground/70 bg-foreground/90" />
          {labels.capital}
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-full bg-amber-300" />
          {labels.unit}
        </span>
        <span className="inline-flex items-center gap-1">
          <span aria-hidden="true" className="text-xs leading-none text-amber-300">
            &#9733;
          </span>
          {labels.marker}
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-sm border-2 border-sky-400 bg-transparent" />
          {labels.border}
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-sm bg-foreground/30" />
          {labels.explored}
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className="rounded-sm" style={{ backgroundColor: FOG_COLOR }} />
          {labels.fog}
        </span>
      </div>
    </section>
  );
}
